// File: src/components/about/Team.js

import React, { useState, useEffect, useCallback } from 'react';
import styles from './Team.module.css';
import ScrollAnimation from '../common/ScrollAnimation';
import { CORE_TEAM, ADVISORS } from '../../config/teamConfig';
import { cn } from '../../utils/helpers';

// ─── Avatar fallback ──────────────────────────────────────────────────────────
function Initials({ name }) {
  const letters = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return (
    <div className={styles.initials}>
      <span>{letters}</span>
    </div>
  );
}

// ─── Team card (grid) ─────────────────────────────────────────────────────────
function TeamCard({ member, index, onOpen }) {
  const [imgError, setImgError] = useState(false);

  return (
    <ScrollAnimation delay={index * 120}>
      <button
        className={styles.card}
        onClick={() => onOpen(member)}
        aria-label={`View ${member.name}'s profile`}
      >
        <div className={styles.cardImage}>
          {imgError ? (
            <Initials name={member.name} />
          ) : (
            <img
              src={member.image}
              alt={member.name}
              onError={() => setImgError(true)}
            />
          )}
          <div className={styles.cardOverlay}>
            <span>View Profile</span>
          </div>
        </div>
        <div className={styles.cardInfo}>
          <h3>{member.name}</h3>
          <p>{member.title}</p>
        </div>
      </button>
    </ScrollAnimation>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function TeamModal({ member, onClose }) {
  const [imgError, setImgError] = useState(false);

  const handleKey = useCallback(
    (e) => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKey);

    // Freeze page scroll (works with Lenis)
    const y = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      window.scrollTo(0, y);
    };
  }, [handleKey]);

  return (
    <div
      className={styles.backdrop}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className={styles.modal} data-lenis-prevent>

        {/* 1 — Sticky close bar */}
        <div className={styles.modalBar}>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4l8 8"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* 2 — Identity panel (never scrolls) */}
        <div className={styles.identity}>
          <div className={styles.identityImage}>
            {imgError ? (
              <Initials name={member.name} />
            ) : (
              <img
                src={member.image}
                alt={member.name}
                onError={() => setImgError(true)}
              />
            )}
          </div>
          <div className={styles.identityText}>
            <h2>{member.name}</h2>
            <p>{member.title}</p>
            {member.linkedin && (
              <a
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkedin}
              >
                LinkedIn →
              </a>
            )}
          </div>
        </div>

        {/* 3 — Scrollable bio */}
        <div className={styles.bio}>
          {member.bio.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>

      </div>
    </div>
  );
}

// ─── Advisors ─────────────────────────────────────────────────────────────────
function Advisors() {
  return (
    <div className={styles.advisors}>
      <ScrollAnimation>
        <h3 className={styles.advisorsTitle}>Advisory Board</h3>
        <p className={styles.advisorsSub}>
          Guided by world-class expertise in science, policy, and sustainability.
        </p>
      </ScrollAnimation>
      <ul className={styles.advisorList}>
        {ADVISORS.map((a, i) => (
          <ScrollAnimation key={a.id} delay={i * 100}>
            <li className={styles.advisorItem}>
              <span className={styles.advisorDot} />
              <div>
                <strong>{a.name}</strong>
                <span>{a.title}</span>
              </div>
            </li>
          </ScrollAnimation>
        ))}
      </ul>
    </div>
  );
}

// ─── Page section ─────────────────────────────────────────────────────────────
function Team() {
  const [active, setActive] = useState(null);

  return (
    <section className={cn('section section-light', styles.section)}>
      <div className="site-container">

        <ScrollAnimation>
          <div className={styles.header}>
            <h2>The People Behind GreenScience</h2>
            <p>Passionate about sustainability, driven by innovation.</p>
          </div>
        </ScrollAnimation>

        <div className={styles.grid}>
          {CORE_TEAM.map((m, i) => (
            <TeamCard key={m.id} member={m} index={i} onOpen={setActive} />
          ))}
        </div>

        <Advisors />
      </div>

      {active && (
        <TeamModal member={active} onClose={() => setActive(null)} />
      )}
    </section>
  );
}

export default Team;